import React, { useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { clearDemoSession, isDemoPersona, seedDemoSession } from '../../demo/core';
import { demoOrganizationForPersona } from '../../demo/data/organizations';

/** Home page of both demo personas. */
const DEMO_HOME = '/';

/**
 * Per-tab ESG UI preferences that could still point at another session's
 * sites, months or widgets. Cleared before the demo session is seeded.
 */
const ESG_PREFERENCE_KEYS = [
  'selectedMonth',
  'selectedStats',
  'dataEntryPreferences',
  'hseWeeklyPreferences',
  'esgReportPreferences',
  'esgMonthlySelectedMetrics',
  'esgMonthlySelectedExtras',
  'esgOverviewSelectedWidgets',
  'esgScopesSelectedWidgets',
  'esgBranchesSelectedWidgets',
  'esgCarbonSelectedWidgets',
  'esgSelectedStats:waste_outlet',
  'esgSelectedStats:recycler',
  'esgSelectedStats:truck',
  'carbonReportStartDate',
  'carbonReportEndDate',
];

/**
 * Public `/demo/:persona` route (`outlet` | `recycler`): starts a demo session
 * in this tab — fake tokens, demo org as the selected organization — then
 * reloads into the real app, which boots through initAuth on the fake API.
 */
const DemoEntry: React.FC = () => {
  const { persona } = useParams<{ persona: string }>();

  useEffect(() => {
    if (!isDemoPersona(persona)) {
      window.location.replace('/login');
      return;
    }
    clearDemoSession();
    for (const key of ESG_PREFERENCE_KEYS) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        /* storage unavailable */
      }
    }
    seedDemoSession(persona, { ...demoOrganizationForPersona(persona) });
    window.location.replace(DEMO_HOME);
  }, [persona]);

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 2,
        textAlign: 'center',
        bgcolor: 'background.default',
      }}
    >
      <CircularProgress sx={{ color: '#0000FE' }} aria-hidden />
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        Preparing your demo…
      </Typography>
      <Typography variant="body2" color="text.secondary" dir="rtl" lang="ar">
        جارٍ تجهيز العرض التجريبي…
      </Typography>
    </Box>
  );
};

export default DemoEntry;
